import type { TabView } from "@e308/ux";
import { BUILDINGS } from "../content/buildings.js";
import { REPEATABLES, SPECIALIZATIONS, TECHNOLOGIES } from "../content/research.js";
import type { SeatObservation } from "../types.js";
import type { AppIntent, AppState } from "./app-source.js";
import { queueComposer, queueView } from "./economy-queue-view.js";
import { dashboard, help } from "./layout-view.js";
import {
  gameAction,
  heading,
  list,
  move,
  type AppNode as Node,
  name,
  range,
  text,
} from "./view-nodes.js";

export function economyTab(
  label: "Town" | "Factories",
  state: AppState,
): TabView<AppIntent, string> {
  const observation = state.observation as SeatObservation;
  return {
    id: label.toLowerCase(),
    label,
    content: dashboard(
      label === "Town" ? town(observation, state) : factories(observation, state),
      label === "Town" ? ["construction-heading", "power-heading"] : ["assembly-heading"],
    ),
  };
}

function town(observation: SeatObservation, state: AppState): readonly Node[] {
  return [
    heading("town-heading", "Town"),
    text(
      "town-brief",
      `${observation.self.name} · Build capacity, keep it powered, protect your Headquarters.`,
    ),
    help(
      "opening-help",
      "Your first shift",
      "Your town already repeats Mining Rigs and Guard Robots. Add generators as Power fills up. Queue Construction Yards to build faster. Visit Borders to deploy your Home Reserve: troops at home do not defend a border. Your Emergency Workshop supplies 1 Metal/s, 1 Construction Work/s, and 10 Power while Headquarters survives.",
    ),
    heading("rates-heading", "Stocks, rates, and shortages", 3),
    diagnostics(observation),
    heading("power-heading", "Power operation", 3),
    ...powerControls(observation),
    priority("Power priority", observation.self.powerPriority, "set-power-priority"),
    heading("construction-heading", "Construction", 3),
    help(
      "construction-help",
      "How queues work",
      "Only the first order works at a time. Repeat sends a finished order to the back of the queue. New orders join the end; use Move up to make them next. Costs are spent gradually as usable building capacity grows.",
    ),
    queueComposer("construction", observation, state.drafts.construction),
    queueView("construction", observation),
    ...constructionSplit(observation),
    heading("restoration-heading", "Restoration", 3),
    queueComposer("restoration", observation, state.drafts.restoration),
    gameAction("emergency-preset", "Emergency restoration preset", {
      kind: "emergency-restoration-preset",
    }),
    queueView("restoration", observation),
    heading("buildings-heading", "Active buildings", 3),
    list(
      "buildings",
      BUILDINGS.map(
        (rule) =>
          `${rule.name}: ${observation.self.buildings[rule.id]} active; ${observation.self.wreckage[rule.id]} wreckage; ${rule.metalCost} Metal / ${rule.workCost} Work each.`,
      ),
    ),
  ];
}

function factories(observation: SeatObservation, state: AppState): readonly Node[] {
  const retool = Math.max(
    0,
    observation.self.manufacturing.factoryWorksRetoolUntilMs - observation.gameTimeMs,
  );
  return [
    heading("factories-heading", "Factories"),
    text(
      "chain",
      "Factory Works → producer factories → Mining Rigs, Construction Yards, and Robot Factories → robots and siege guns.",
    ),
    heading("factory-works-heading", "Factory Works output", 3),
    help(
      "factory-help",
      "The production chain",
      "Build or research producer factories to automate growth. Factory Works makes Rig Factories, Yard Factories, or Robot Factories. Changing its output pauses all its production for 10 game seconds.",
    ),
    text(
      "retool",
      retool > 0
        ? `Retooling: ${Math.ceil(retool / 1_000)} seconds remain.`
        : "Factory Works is ready.",
    ),
    ...factoryControls(observation),
    priority("Metal spending priority", observation.self.metalPriority, "set-metal-priority"),
    heading("assembly-heading", "Assembly", 3),
    queueComposer("assembly", observation, state.drafts.assembly),
    queueView("assembly", observation),
    list(
      "recipes",
      BUILDINGS.filter((rule) => rule.outputs.length > 0).map(
        (rule) =>
          `${rule.name}: ${rule.outputs.map((output) => `${output.perSecond} ${name(output.quantity)}/s${output.consumesMetalPerSecond ? ` for ${output.consumesMetalPerSecond} Metal/s` : ""}`).join(", ")}.`,
      ),
    ),
  ];
}

function powerControls(observation: SeatObservation): readonly Node[] {
  const value = observation.self.powerOperation;
  return Object.entries(value).map(([key, current]) =>
    range(`power-${key}`, `${name(key)} ${current}%`, current, (next) => ({
      kind: "set-power",
      industry: key === "industry" ? next : value.industry,
      defenses: key === "defenses" ? next : value.defenses,
      militaryProduction: key === "militaryProduction" ? next : value.militaryProduction,
      research: key === "research" ? next : value.research,
    })),
  );
}

function constructionSplit(observation: SeatObservation): readonly Node[] {
  const split = observation.self.constructionSplit;
  return [
    range(
      "new-construction",
      `Build new ${split.newConstruction}%`,
      split.newConstruction,
      (next) => ({
        kind: "set-construction-split",
        newConstruction: next,
        restoration: Math.min(split.restoration, 100 - next),
      }),
    ),
    range("restoration", `Restore wreckage ${split.restoration}%`, split.restoration, (next) => ({
      kind: "set-construction-split",
      newConstruction: Math.min(split.newConstruction, 100 - next),
      restoration: next,
    })),
  ];
}

function factoryControls(observation: SeatObservation): readonly Node[] {
  const split = observation.self.factoryWorksSplit;
  return Object.entries(split).map(([key, current]) =>
    range(`factory-${key}`, `${name(key)} ${current}%`, current, (next) => {
      const result = allocationFit(split, key, next);
      return {
        kind: "set-factory-works",
        "rig-factory": result["rig-factory"] ?? 0,
        "yard-factory": result["yard-factory"] ?? 0,
        "robot-factory": result["robot-factory"] ?? 0,
      };
    }),
  );
}

export function allocationFit(
  current: Readonly<Record<string, number>>,
  selected: string,
  value: number,
): Record<string, number> {
  let remaining = 100 - value;
  const result: Record<string, number> = { [selected]: value };
  for (const [key, amount] of Object.entries(current))
    if (key !== selected) {
      result[key] = Math.min(amount, remaining);
      remaining -= result[key] ?? 0;
    }
  return result;
}

function priority(
  label: string,
  values: readonly string[],
  kind: "set-power-priority" | "set-metal-priority",
): Node {
  return {
    kind: "infobox",
    id: kind,
    title: label,
    initiallyOpen: true,
    content: values.flatMap((value, index) => [
      text(`${kind}-${value}`, `${index + 1}. ${name(value)}`),
      ...(index > 0
        ? [
            gameAction(`${kind}-${value}-up`, "Move up", {
              kind,
              priority: move(values, index, index - 1) as never,
            }),
          ]
        : []),
    ]),
  };
}

function diagnostics(observation: SeatObservation): Node {
  const economy = observation.self.economy;
  const lines: Node[] = [
    text(
      "rate-metal",
      `Metal ${observation.self.metal}; gross ${economy.grossMetal}/s; spent ${economy.metalSpent}/s; net ${economy.netMetal}/s.`,
    ),
    text(
      "rate-work",
      `Power ${economy.availablePower} available; ${economy.unusedPower} unused. Work ${economy.constructionWork}/s; Assembly ${economy.assemblyWork}/s; RP ${economy.researchPoints}/s.`,
    ),
    ...Object.entries(economy.power).map(([id, value]) =>
      text(
        `rate-power-${id}`,
        `${name(id)} Power: requested ${value.requested}, received ${value.received}. ${value.explanation}`,
      ),
    ),
    ...Object.entries(economy.power).flatMap(([id, value]) =>
      value.status === "power-limited"
        ? [
            gameAction(`fix-power-${id}`, `Move ${name(id)} to first Power priority`, {
              kind: "set-power-priority",
              priority: first(observation.self.powerPriority, id) as never,
            }),
          ]
        : [],
    ),
    ...Object.entries(economy.metalLanes).map(([id, value]) =>
      text(
        `rate-metal-${id}`,
        `${name(id)} Metal: requested ${value.requested}, received ${value.received}. ${value.explanation}`,
      ),
    ),
    ...Object.entries(economy.metalLanes).flatMap(([id, value]) =>
      value.status === "metal-limited"
        ? [
            gameAction(`fix-metal-${id}`, `Move ${name(id)} to first Metal priority`, {
              kind: "set-metal-priority",
              priority: first(observation.self.metalPriority, id) as never,
            }),
          ]
        : [],
    ),
    ...rateMultipliers(observation).map((line, index) => text(`rate-multiplier-${index}`, line)),
  ];
  return {
    kind: "infobox",
    id: "rate-breakdown",
    title: "Exact rate and shortage breakdown",
    initiallyOpen: true,
    content: lines,
  };
}

function first(values: readonly string[], selected: string): string[] {
  return [selected, ...values.filter((value) => value !== selected)];
}

function rateMultipliers(observation: SeatObservation): readonly string[] {
  const research = observation.self.research;
  const definitions = [
    ...TECHNOLOGIES.filter((entry) => research.completed.includes(entry.id)),
    ...SPECIALIZATIONS.filter(
      (entry) =>
        entry.id === research.industrialSpecialization ||
        entry.id === research.militarySpecialization,
    ),
  ];
  const named = definitions.flatMap((entry) =>
    entry.effect.kind === "multiply"
      ? [`${entry.name}: ×${entry.effect.factor} to ${entry.effect.targets.map(name).join(", ")}.`]
      : [],
  );
  const repeatable = REPEATABLES.flatMap((entry) => {
    const level = research.repeatableLevels[entry.id];
    return level > 0
      ? [
          `${entry.name} level ${level}: ×${entry.factorPerLevel} per level to ${entry.targets.map(name).join(", ")}.`,
        ]
      : [];
  });
  return named.length + repeatable.length > 0
    ? [...named, ...repeatable]
    : ["No researched rate multipliers are active."];
}
